import cloneDeep from 'lodash/cloneDeep';
import {Component} from 'panel';
import stringify from 'json-stable-stringify';
import ace from 'brace';
import 'brace/ext/language_tools';
import pick from 'lodash/pick';

import {CustomProperty} from 'common/types/reports/query';
import {customPropertiesStore} from 'common/report/stores/custom-properties';
import {customPropertyClient} from 'common/report/queries/app-api/custom-properties-client';
import {customPropertiesValidateClient} from 'common/report/queries/query-api/custom-properties-validate-client';
import {emitTrackEvent} from 'common/util/dom';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {getOwnAttr, MeasureUtil} from 'common/util';
import {getAlphaLabelFromIndex} from 'common/util';
import {QUERY_BUILDER_RESOURCE_TYPE_MAPPING} from 'common/widgets/property-filter-menu/models/insights-filter';
import mpFonts from 'common/stylesheets/mixins/fonts.json';
import {currentProjectStore} from 'common/report/stores/project-details';
import 'common/util/ace/patches';
import 'common/util/ace/theme/mixpanel';
import arbselectorDefs from 'common/util/tern/definitions/arb-selector.json';
import {
  buildCreateEntityModalAttrs,
  isSharingEnabledForProject,
  normalizeCustomPropertyEntity,
} from 'common/report/util/saved-entities';
import {EntityType, SharableEntityType} from 'common/report/util/saved-entities/types';
import {PRIMITIVE_MATH_TYPES, ResourceType} from 'common/report/constants';
import {FeatureGate, RestrictEntitySaving} from 'common/report/constants';
import {StackableComponent, Visibility} from 'common/components/base/stackable';
import {promisifyModal} from 'common/components/modal/utils';
import {propertiesUtils} from 'common/report/util/properties';
import {promptSharing} from 'common/widgets/sharing';
import {ApiResponseError} from 'common/util/shared-client';
import {QueryEventEntry} from 'common/query-builder/query-entry-section/types';
import type {QueryBlock} from 'common/query-builder/query-block';
import {TypecastPillAction} from 'common/query-builder/query-block/properties-menu/types';
import {MenuType, QueryBlockAttrs} from 'common/query-builder/query-block/types';
import {QueryBlockThemeExtras} from 'common/query-builder/query-block/constants';

import {CustomPropertyBuilderErrors, CustomPropertyBuilderState} from './types';
import {CustomPropertyCompleter} from './completer';
import {getInlineCustomPropertyId, isInlineCustomProperty, isPersistedCustomProperty} from './util';
import template from './index.jade';
import css from './index.inline.styl';
import {TRACKING_EVENTS} from './constants';

const {Autocomplete} = ace.acequire(`ace/autocomplete`);
const {TokenIterator} = ace.acequire(`ace/token_iterator`);
const range = ace.acequire(`ace/range`);

const DISPATCHED_EVENTS = {
  CANCEL: `cancel`,
  APPLY: `apply`,
  DELETE: `delete`,
};
const EMPTY_ENCODED_PROPERTY = `@"""{}"""@`;
const PERSISTENT_EMPTY_ENCODED_PROPERTY = `@"""{ }"""@`;
const EXAMPLES_INLINE_PROPERTIES = {
  'Resize range': `// Select a numeric property. In this example the property is assumed to
// be the time a user took to do an action, in minutes. This formula
// groups durations based on specified ranges.
IFS(
  ${PERSISTENT_EMPTY_ENCODED_PROPERTY} < 60, "Less than 1 hour",
  ${PERSISTENT_EMPTY_ENCODED_PROPERTY} < 120, "More than 1 hour, less than 2 hours",
  ${PERSISTENT_EMPTY_ENCODED_PROPERTY} < 240, "More than 2 hours, less than 4 hours",
  ${PERSISTENT_EMPTY_ENCODED_PROPERTY} < 360, "More than 4 hours, less than 6 hours",
  ${PERSISTENT_EMPTY_ENCODED_PROPERTY} >= 360, "More than 6 hours"
)`,
  'Merge values': `// Select a string property. In this example the property is assumed to
// be a string with values like "iOS 9", "iOS 10", "Mozilla", "Firefox",
// that can be grouped into larger categories.
IFS(
  "iOS" in ${PERSISTENT_EMPTY_ENCODED_PROPERTY}, "Apple",
  "Mozilla" in ${PERSISTENT_EMPTY_ENCODED_PROPERTY} or "Firefox" in ${PERSISTENT_EMPTY_ENCODED_PROPERTY}, "Firefox",

  // Since this last case is always TRUE, if none of the above
  // conditions are met, this formula will evaluate to the property
  // selected here
  TRUE, ${PERSISTENT_EMPTY_ENCODED_PROPERTY}
)`,
  'Extract regex': `// Select a string property. The property is assumed to be a
// URL (e.g. "https://mixpanel.com/report/123"). This formula extracts
// "mixpanel.com" from the URL using a regular expression. There is
// also a REGEX_MATCH() function.
REGEX_EXTRACT(${PERSISTENT_EMPTY_ENCODED_PROPERTY}, "https?:\\/\\/([^\\/]+)", 1)`,
  'Replace regex': `// Select a string property. The property is assumed to be a dollar
// amount with commas in it (e.g. "$4,567"). This formula replaces the
// dollar sign and commas with the empty string, allowing the values to be
// cast to numbers and compared.
LET(amount, NUMBER(REGEX_REPLACE(${PERSISTENT_EMPTY_ENCODED_PROPERTY}, "[$,]", "")),
  IFS(
    amount < 200, "Low",
    amount < 1000, "Medium",
    TRUE, "High",
  )
)`,
  'Days since sign-up': `// Select two different datetime properties.
// A good selection for the first property would be a user property that
// records the user's sign up date, and a good selection for the second
// property would be the "Date" event property. With these selections
// this custom property will evaluate to the number of days after sign
// up that a user performed each event.
DATEDIF(${PERSISTENT_EMPTY_ENCODED_PROPERTY}, ${PERSISTENT_EMPTY_ENCODED_PROPERTY}, "D")`,
  'Email Domain': `// You can split a string into different parts based on a user-specified delimiter using the SPLIT function.
// Choose a string property (e.g. dwightschrute@dundermifflin.com), and then the delimiter (in the case of an email domain, it would be @) and then which delimited part you'd like (2, because we want the part after @)
SPLIT(${PERSISTENT_EMPTY_ENCODED_PROPERTY}, "@", 2)`,
};
const HELP_DOC = `https://help.mixpanel.com/hc/en-us/articles/360030848432`;

const COMPONENT_NAME = `mp-custom-property-modal`;

const CUSTOM_PROPERTY_RESOURCE_TYPES = {
  [ResourceType.Event]: QUERY_BUILDER_RESOURCE_TYPE_MAPPING[ResourceType.Event],
  [ResourceType.User]: QUERY_BUILDER_RESOURCE_TYPE_MAPPING[ResourceType.User],
};

const BEHAVIOR_QUERY_ENTRY_FIELDS = [
  `event`,
  `dateRange`,
  `dataGroupId`,
  `filters`,
  `filtersOperator`,
  `property`,
  `aggregationOperator`,
];

const CLASS_SYNTAX_ERROR = `ace-highlight-syntax-error`;
const CLASS_PROPERTY_DROPDOWN = `ace_fold_property_dropdown`;

const DEFAULT_CHARACTER_WIDTH = 8.802111206054688;

let autocompleteSingleton = null;

function escapeHtml(text) {
  const textElem = document.createTextNode(text);
  const pElem = document.createElement(`p`);
  pElem.appendChild(textElem);
  return pElem.innerHTML;
}

export async function promptCustomProperties(
  attrs,
): Promise<{appliedValue: any; didClose: boolean; deletedId: string | number}> {
  const closeEv = await promisifyModal({
    tagName: `mp-custom-property-modal`,
    attrs,
    closeEvents: [`apply`, `cancel`, `change`, `delete`],
  });

  return {
    appliedValue: closeEv.type === `apply` ? closeEv.detail?.property : null,
    didClose: closeEv.type === `cancel` || (closeEv.type === `change` && closeEv.detail.state === Visibility.Closed),
    deletedId: closeEv.type === `delete` ? closeEv.detail.customPropertyId : null,
  };
}

// property.label is trustworthy when the property is first generated
// by an mp-query-block, but if the user saves a custom property and
// comes back to it the label may be out of date (e.g. it may refer to
// a custom property which has been renamed), in cases where we need
// to make sure that we have the most up to date property name, this
// function will recompute the property name
function computePropertyName(property) {
  // get the latest copy of the custom property, not the stored copy
  const customProperty = property.customPropertyId ? customPropertiesStore.value[property.customPropertyId] : null;
  let parts: string[] = [property.name || property.value];

  if (Array.isArray(property.propertyObjectKey)) {
    parts = [...parts, ...property.propertyObjectKey];
  }

  parts = parts.map((value) =>
    propertiesUtils.renameProperty(value, property.resourceType, {
      dataGroupId: property.dataGroupId,
      customProperty,
    }),
  );
  return parts.join(`.`);
}

export class CustomPropertyBuilder extends Component<CustomPropertyBuilderState> {
  editor: Editor;
  private currentProjectStoreCallback = () => this.update();

  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      'behavioral-properties-mode': `boolean`,
      'default-name': `string`,
      'enable-workspace-filter-restrictions': `boolean`,
      'event-names': {
        type: `json`,
        default: null,
        description: `when set, displayed properties belong only to the set of events passed`,
      },
      property: {
        type: `json`,
        default: null,
        description: `the inline or persisted custom property to render.`,
      },
      'project-id': `number`,
      'user-id': `number`,
      open: `boolean`,
      'track-name': {
        type: `string`,
        description: `If specified, dispatches '[mp-custom-property-modal] <event>' bubbled events that can be listened for tracking with "Report name" as a property`,
      },
      'workspace-id': `number`,
    };
  }

  constructor() {
    super();
  }

  static get dispatchedEvents() {
    return Object.values(DISPATCHED_EVENTS);
  }

  attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);
    if (attr === `property`) {
      const property = this.attr(attr) || {};
      const {customPropertyId} = property;
      let {customProperty, resourceType} = property;
      if (!resourceType) {
        if (isPersistedCustomProperty(customPropertyId) && !customProperty) {
          customProperty = customPropertiesStore.value[customPropertyId];
          // make a copy to not modify the store directly
          customProperty = cloneDeep(customProperty);
        }
        if (customProperty) {
          resourceType = customProperty.resourceType;
        }
      }
      if (!customProperty) {
        customProperty = this.newCustomProperty(this.helpers.isBehavioralProperty());
      }
      // custom properties use 'events' and 'people' instead of 'event' and 'user'
      // TODO (safdar): change this to DTRT when/if custom properties are migrated to the QueryEntry format
      if (Object.keys(CUSTOM_PROPERTY_RESOURCE_TYPES).includes(resourceType)) {
        resourceType = CUSTOM_PROPERTY_RESOURCE_TYPES[resourceType];
      }
      this.update({
        customPropertyId,
        customProperty,
        resourceType,
        toCreate: null,
      });
      this.updateQueryEntriesFromBehavior();
    }
  }

  connectedCallback() {
    super.connectedCallback();
    currentProjectStore.subscribe(this.currentProjectStoreCallback).fetch();
    if (!this.helpers.isBehavioralProperty()) {
      // this is hacky, but ace editor injects the style tags into the global space so we grab them
      // from there and inject the stylesheets into the style tag in the shadowdom we create here
      const aceCss = [`ace_editor.css`, `ace-tm`, `ace-mixpanel`]
        .map((id) => document.getElementById(id)?.innerHTML)
        .join(` `);
      const styleTag = document.createElement(`style`);
      // this style content is already present in the page, we are pushing the content into the shadow dom
      // eslint-disable-next-line no-unsanitized/property
      styleTag.innerHTML = aceCss;
      this.el.appendChild(styleTag);
    }
  }

  disconnectedCallback(): void {
    currentProjectStore.unsubscribe(this.currentProjectStoreCallback);
    super.disconnectedCallback();
  }

  get config() {
    return {
      ...super.config,
      css,
      useShadowDom: true,
      template,
      defaultState: {
        activePropertyIndex: null,
        customPropertyId: null,
        customProperty: this.newCustomProperty(),
        errors: {},
        extractionError: null,
        queryEntries: [] as QueryEventEntry[],
        toDelete: false,
        autocompleteOpen: false,
        autocompletePopup: null,
        currentAutocompleteLine: null,
        toCreate: null,
      },
      helpers: {
        getBehavioralAggregationMethods: () => [
          PRIMITIVE_MATH_TYPES.TOTAL,
          PRIMITIVE_MATH_TYPES.AVERAGE,
          PRIMITIVE_MATH_TYPES.UNIQUE_VALUES,
          PRIMITIVE_MATH_TYPES.MOST_FREQUENT,
          PRIMITIVE_MATH_TYPES.FIRST_VALUE,
        ],
        isSavingDisabled: () => this.helpers.isBehavioralProperty() && this.getQueryEntries().length === 0,
        isSavingRestricted: () => !currentProjectStore.value?.canSaveEntity(RestrictEntitySaving.CustomProperties),
        openPricingUpsell: () => {
          this.track(TRACKING_EVENTS.CLICK_UPSELL, {
            'Feature Limit': FeatureGate.RestrictEntitySaving,
            'Entity Type': `custom_properties`,
          });
          window.open(`https://mixpanel.com/pricing/`, `_blank`).focus();
        },
        getBehavioralSelectorTypes: () => {
          return [
            {
              value: ResourceType.Event,
              includeDateRange: true,
            },
          ];
        },
        buildCreateEntityModalAttrs: () => {
          return buildCreateEntityModalAttrs({
            projectId: this.attr(`project-id`),
            entityType: EntityType.CustomProperty,
            entity: normalizeCustomPropertyEntity(this.state.customProperty),
            modalOpen: this.attr(`open`),
            isEditing: false,
            isDuplicating: false,
            showShareManagement: this.helpers.hasSharingFeatureFlag(),
          });
        },

        isBehavioralProperty: () =>
          !!getOwnAttr(this.state.customProperty, `behavior`) || this.attr(`behavioral-properties-mode`),

        tooltipClicked: () => window.open(HELP_DOC, ``).focus(),
        updateName: (ev) => {
          this.updateField(`name`, ev.target.value, Boolean);
        },
        updateDescription: (ev) => {
          const customProperty = {...this.state.customProperty, description: ev.target.value};
          this.update({customProperty});
        },
        getName: () => this.state.customProperty.name,
        getDescription: () => this.state.customProperty.description,
        isEdit: () => {
          const property = this.attr(`property`);
          return property && (property.customPropertyId || property.customProperty);
        },
        isPersisted: () => isPersistedCustomProperty(this.state.customPropertyId),
        hasSharingFeatureFlag: () => isSharingEnabledForProject(currentProjectStore.value),
        getHeaderText: () => {
          const resourceTypeName = this.helpers.isBehavioralProperty() ? `Behavior` : `Custom Property`;
          let action = `Create a`;
          if (this.helpers.isPersisted()) {
            action = `Update`;
          } else if (isInlineCustomProperty(this.state.customPropertyId)) {
            action = `Edit`;
          }
          return `${action} ${resourceTypeName}`;
        },
        getResourceType: () => this.state.resourceType,
        getResourceTypeText: () => {
          return {
            [CUSTOM_PROPERTY_RESOURCE_TYPES[ResourceType.User]]: `Profile`,
            [CUSTOM_PROPERTY_RESOURCE_TYPES[ResourceType.Event]]: `Event`,
          }[this.helpers.getResourceType()];
        },
        modalStateChange: (ev) => {
          if (ev.detail && ev.detail.state === `closed`) {
            this.close();
          }
        },
        getPersistedCustomPropertyNames: () => {
          return Object.entries(customPropertiesStore.value)
            .filter((prop) => isPersistedCustomProperty(prop[0]))
            .map((prop) => prop[1].name);
        },
        getComposedProperties: () => this.getComposedProperties(),
        getDisplayFormula: () => this.state.customProperty.displayFormula,
        closeModal: () => this.close(),
        setToDelete: () => this.update({toDelete: true}),
        deleteProperty: () => {
          // TODO: handle network blip, etc, when deleting a property
          this.deleteProperty(this.state.customPropertyId);
          this.dispatchEvent(
            new CustomEvent(DISPATCHED_EVENTS.DELETE, {
              detail: {
                customPropertyId: this.state.customPropertyId,
              },
            }),
          );
        },
        handleDeleteConfirmChange: (visibility) => {
          if (visibility !== `open`) {
            this.update({toDelete: false});
          }
        },
        getExampleOptions: () => Object.keys(this.helpers.getExamples()),
        handleInsertExample: (ev) => {
          this.trackWithState(
            TRACKING_EVENTS.INSERT_EXAMPLE,
            this.state.customPropertyId,
            this.state.customProperty,
            false,
            {},
            {
              Example: ev.detail.value,
            },
          );
          this.editor.setDisplayFormula(this.helpers.getExamples()[ev.detail.value], {});
          this.editor.focus();
        },
        // TODO opslead
        // eslint-disable-next-line sonarjs/cognitive-complexity
        applyProperty: async (toCreate) => {
          let errors: CustomPropertyBuilderErrors = {};
          if (!this.helpers.getName()) {
            errors.name = `Required`;
          }
          if (!this.helpers.isBehavioralProperty()) {
            if (!this.helpers.getDisplayFormula()) {
              errors.displayFormula = `Required`;
            }
            const composedPropertiesError = this.validateComposedProperties();
            if (composedPropertiesError !== null) {
              errors.composedProperties = composedPropertiesError;
            }
          }
          if (Object.entries(errors).length > 0) {
            this.trackWithState(
              TRACKING_EVENTS.SAVE,
              this.state.customPropertyId,
              this.state.customProperty,
              toCreate,
              errors,
            );
            this.update({errors});
            return;
          }

          let customProperty = {
            ...this.state.customProperty,
            resourceType: this.state.resourceType,
          };

          let result;
          let storeUpdate = {};
          const projectId = this.attr(`project-id`);
          let {customPropertyId} = this.state;
          try {
            if (this.state.extractionError !== null) {
              throw new Error(`ExtractionError for field:displayFormula - ${this.state.extractionError}`);
            }
            if (isPersistedCustomProperty(customPropertyId)) {
              // update
              result = await customPropertiesStore.put(customPropertyId, {
                projectId,
                customProperty,
                customPropertyId,
              });
            } else {
              // inline
              const response = await customPropertyClient.query({
                projectId,
                customProperty,
                validate: true,
                workspaceId: this.attr(`workspace-id`),
              });
              if (response.status === `error`) {
                throw new ApiResponseError(response, response.error);
              } else {
                result = response.results;
              }
            }
          } catch (err) {
            errors = {[err.response.field]: err.response.error};
            this.trackWithState(TRACKING_EVENTS.SAVE, customPropertyId, customProperty, toCreate, errors);
            this.update({errors});
            return;
          }

          const propertyType = result && result.propertyType;
          customProperty = {...customProperty, propertyType};

          if (!result || !result.customPropertyId) {
            // inline, we give it a session only UID if none has been given before
            customPropertyId = getInlineCustomPropertyId(customPropertyId);
            // and manually set it to the cache
            storeUpdate = {[customPropertyId]: customProperty as CustomProperty};
          }

          if (toCreate) {
            this.update({
              toCreate: customProperty,
            });
          } else {
            customPropertiesStore.update(storeUpdate);
            this.trackWithState(TRACKING_EVENTS.SAVE, customPropertyId, customProperty, toCreate, {});
            this.dispatchEvent(
              new CustomEvent(DISPATCHED_EVENTS.APPLY, {
                detail: {
                  property: {
                    customProperty,
                    customPropertyId,
                    resourceType: this.state.resourceType,
                    propertyType,
                    type: propertyType,
                    value: customProperty.name,
                  },
                },
              }),
            );
          }
        },
        handleCreate: async (ev) => {
          const {name, description} = ev.detail.value;
          const customProperty: CustomProperty = {
            ...this.state.toCreate,
            exampleValue: ``,
            name,
            description,
          };
          const projectId = this.attr(`project-id`);
          const result = await customPropertiesStore.post({
            projectId,
            customProperty,
            workspaceId: this.attr(`workspace-id`),
          });
          if (result && result.customPropertyId) {
            const customPropertyId = result.customPropertyId;
            if (this.state.customPropertyId && !isPersistedCustomProperty(this.state.customPropertyId)) {
              // we just saved an inline customProperty to a persisted one
              customPropertiesStore.update({
                [this.state.customPropertyId]: {
                  ...customPropertiesStore.value[this.state.customPropertyId],
                  newCustomPropertyId: customPropertyId,
                },
              });
            }
            this.trackWithState(TRACKING_EVENTS.SAVE, customPropertyId, customProperty, true, {});
            this.dispatchEvent(
              new CustomEvent(DISPATCHED_EVENTS.APPLY, {
                detail: {
                  property: {
                    customProperty,
                    customPropertyId,
                    resourceType: this.state.resourceType,
                    propertyType: customProperty.propertyType,
                    propertyDefaultType: customProperty.propertyType,
                    type: customProperty.propertyType,
                    value: customProperty.name,
                  },
                },
              }),
            );

            // When Save and Share button is clicked, open a sharing modal
            if (ev.type === `saveAndShare`) {
              await promptSharing({
                type: SharableEntityType.CustomProperty,
                id: customPropertyId,
                'project-id': projectId,
              });
            }
          }
        },
        handleCancelCreate: () => {
          this.update({toCreate: null});
        },
        handleEditorConnect: (ev) => {
          this.update({
            autocompletePopup: ev.detail.autocompletePopup,
          });
        },
        handleEditorAutocompleteChange: (ev) => {
          const updates = {tooltipData: null};
          if (ev.detail.autocompleteOpen !== undefined) {
            updates[`autocompleteOpen`] = ev.detail.autocompleteOpen;
          }
          if (ev.detail.currentAutocompleteLine !== undefined) {
            updates[`currentAutocompleteLine`] = ev.detail.currentAutocompleteLine;
          }
          if (ev.detail.autocompleteOpen || this.state.autocompleteOpen) {
            if (ev.detail.menuTooltipData !== undefined) {
              updates[`tooltipData`] = ev.detail.menuTooltipData;
            }
          } else {
            if (ev.detail.cursorTooltipData !== undefined) {
              updates[`tooltipData`] = ev.detail.cursorTooltipData;
            }
          }
          this.update(updates);
        },
        handleValueChange: (ev) => {
          ev.stopPropagation();
          this.updateField(`displayFormula`, ev.detail.displayFormula, Boolean);
          this.update({extractionError: ev.detail.extractionError});
          this.updateComposedProperties(ev.detail.composedProperties);
        },
        handleEditorElementInsert: (vnode) => {
          this.editor = vnode.elm;
        },
        getQueryEntries: () => this.getQueryEntries(),
        handleQueryEventsSectionUpdate: (ev: CustomEvent) => {
          this.updateQueryEntries(ev.detail.entries);
          this.updateBehaviorFromQueryEntries();
        },
        getExamples: () => EXAMPLES_INLINE_PROPERTIES,
      },
    };
  }

  close() {
    this.trackWithState(
      TRACKING_EVENTS.CANCEL,
      this.state.customPropertyId,
      this.state.customProperty,
      false,
      {},
      {had_interaction: this.editor?.hadInteraction},
    );
    this.dispatchEvent(new CustomEvent(DISPATCHED_EVENTS.CANCEL));
  }

  getComposedProperties() {
    return this.state.customProperty.composedProperties;
  }

  updateComposedProperties(composedProperties) {
    this.updateField(`composedProperties`, composedProperties, (value) => Object.entries(value).length > 1);
  }

  validateComposedProperties() {
    const composedProperties = this.getComposedProperties();
    for (const letter of Object.keys(composedProperties)) {
      const property = composedProperties[letter];
      // TODO: should check for recursive references within behaviors as well
      if (
        property.customPropertyId &&
        this.state.customPropertyId &&
        property.customPropertyId === this.state.customPropertyId
      ) {
        return `Variable ${letter} refers to this property. Properties cannot refer to themselves.`;
      }
      if (
        property.customPropertyId &&
        this.state.customProperty.referencedBy &&
        this.state.customProperty.referencedBy.includes(property.customPropertyId)
      ) {
        return `Variable ${letter} cannot refer to property '${property.label}' because '${property.label}' already refers to this property.`;
      }
    }
    return null;
  }

  getQueryEntries() {
    return this.state.queryEntries;
  }

  updateQueryEntries(queryEntries: QueryEventEntry[]) {
    this.update({queryEntries});
  }

  updateBehaviorFromQueryEntries() {
    this.updateField(
      `behavior`,
      {
        ...pick(this.getQueryEntries()[0], BEHAVIOR_QUERY_ENTRY_FIELDS),
      },
      Boolean,
    );
  }

  updateQueryEntriesFromBehavior() {
    const queryEntries: QueryEventEntry[] = [];
    if (this.state.customProperty.behavior) {
      queryEntries.push({
        ...(pick(this.state.customProperty.behavior, BEHAVIOR_QUERY_ENTRY_FIELDS) as QueryEventEntry),
        type: ResourceType.Event,
      });
    }
    this.updateQueryEntries(queryEntries);
  }

  updateField(field, value, toClearErrorFunc) {
    const customProperty = {...this.state.customProperty, [field]: value};
    const {errors} = this.state;
    if (toClearErrorFunc(value) && errors[field]) {
      errors[field] = null;
    }
    this.update({customProperty, errors});
  }

  deleteProperty(customPropertyId) {
    customPropertiesStore.delete(customPropertyId, {
      projectId: this.attr(`project-id`),
      customPropertyId,
    });
  }

  newCustomProperty(forBehavioralProperty = false) {
    if (forBehavioralProperty) {
      return {
        name: this.attr(`default-name`),
        description: ``,
        behavior: null,
      } as CustomProperty;
    } else {
      return {
        name: this.attr(`default-name`),
        description: ``,
        displayFormula: ``,
        composedProperties: {},
      } as CustomProperty;
    }
  }

  track(event, props) {
    emitTrackEvent(this, {
      eventName: event,
      eventPrefix: COMPONENT_NAME,
      props: {
        ...props,
        project_id: this.attr(`project-id`),
        tab: this.attr(`track-name`),
      },
      cancelable: false,
    });
  }

  // TODO opslead
  // eslint-disable-next-line sonarjs/cognitive-complexity
  trackWithState(
    eventType: string,
    customPropertyId,
    customProperty: CustomProperty,
    toCreate,
    errors,
    extraProps = {},
  ) {
    const {name, displayFormula, resourceType, propertyType} = customProperty;
    const keywords = Object.keys(arbselectorDefs)
      .filter((keyword) => !keyword.startsWith(`!`))
      .map((keyword) => (keyword.endsWith(`()`) ? keyword.slice(0, -2) : keyword));
    const functionsUsed = keywords.filter((keyword) => {
      const regex = new RegExp(`\\b${keyword}\\b\\s*\\(`, `gi`);
      // eslint-disable-next-line @typescript-eslint/prefer-regexp-exec
      return regex.test(displayFormula || ``);
    });
    const errorCount = Object.entries(errors).length;

    // populate behavioral property tracking data
    if (this.helpers.isBehavioralProperty() && this.getQueryEntries().length) {
      const entry = this.getQueryEntries()[0];
      extraProps = {
        ...extraProps,
        'is behavioral property': true,
        Aggregator: entry.aggregationOperator,
        'Per-user aggregator': entry.aggregationOperatorPerUser,
        Property: entry.property?.label,
        '# of filters': entry.filters.length,
        'filters operator': entry.filtersOperator,
        'date range from': entry.dateRange.from || null,
        'date range to': entry.dateRange.to || null,
        'date range type': entry.dateRange.type,
        'window unit': entry.dateRange.window ? entry.dateRange.window.unit : null,
        'window value': entry.dateRange.window ? entry.dateRange.window.value : null,
      };
    } else {
      extraProps = {
        ...extraProps,
        'is behavioral property': false,
        displayFormula,
        [`# of composedProperties`]: this.getComposedProperties()
          ? Object.entries(this.getComposedProperties()).length
          : 0,
      };
    }

    const props = {
      customPropertyId,
      name,
      persisted: toCreate || customPropertyId || false,
      resourceType,
      propertyType,
      ...(errorCount > 0 ? {errors} : {}),
      'functions used': functionsUsed,
      ...extraProps,
    };

    this.track(eventType, props);
  }
}

// TODO config accesses attr, need to fix before moving to react
export const CustomPropertyBuilderEl = defineAndMakeJsxFactory(COMPONENT_NAME, CustomPropertyBuilder);

function isPropertyToken(tokenType: string): boolean {
  return tokenType.includes(`encoded_property`);
}

/*
 * This class is a wrapper around Ace editor meant for writing selector-language formulas.
 * For the most part it uses standard Ace APIs to make this experience work, but here are
 * some of the more complex things it does:
 * - Inlining property-drop-downs via Ace editor folds: When writing a formula with this
 *   editor users can insert mp-query-block components inline in the editor which represent
 *   properties. Inlining components in the text editor is not something Ace supports
 *   natively. In the Ace Document these inline components are recorded by a string that
 *   matches the 'encoded_property' token in the selector tokenizer. To render mp-query-blocks
 *   in place of this text we interfere with Aces pre and post render behavior ("render" here
 *   refers to Ace's construction of the <span>s that make up the text of the document.
 *   In order to properly resize the 'encoded_property' text to the size of the mp-query-block
 *   we have a pre-render hook which re-configured Ace's fold-management to "fold" the
 *   encoded_property text into a smaller size. In order to render the mp-query-blocks
 *   we have a post-render hook which searches the DOM for <span>s with the ace_fold class
 *   and creates mp-query-block components which are absolutely-positioned in the same
 *   location on the page. This means: We cannot enable Ace's real code-folding support
 *   as we have hijacked it's functionallity for another purpose. We cannot enable a
 *   scrollbar for the text box since the mp-query-builders are not children of ace's
 *   scroller component (for overflow reasons).
 */
export class Editor extends Component<EmptyObj> {
  editor: any;
  idleTimeout: ReturnType<typeof setTimeout>;
  ready: boolean;

  lastHoveredFold: any;
  emptyMessageElem: HTMLElement | null;
  composedProperties: any;

  // used for P3 tracking to see if the user interacted with the formula box
  hadInteraction: boolean;

  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      'composed-properties': {
        type: `string`,
        default: `{}`,
      },
      'display-formula': `string`,
      'event-names': {
        type: `json`,
        default: null,
        description: `when set, displayed properties belong only to the set of events passed`,
      },
      'enable-workspace-filter-restrictions': `boolean`,
      'project-id': `number`,
      'resource-type': {
        enum: Object.values(CUSTOM_PROPERTY_RESOURCE_TYPES),
        required: true,
        type: `string`,
      },
      'track-name': `string`,
      'user-id': `number`,
      'workspace-id': `number`,
    };
  }

  setDisplayFormula(displayFormula, properties) {
    this.populateDisplayFormula(displayFormula, properties);
  }

  setComposedProperties(properties) {
    this.composedProperties = properties;
    this.onChange();
  }

  private monkeyPatchFoldLogic() {
    // We need to disable Ace's normal fold management (e.g. clicking on a fold automatically
    // expands it) to stop it from conflicting with how we abuse it to resize encoded_property
    // text. Here we replace some fold management functions with no-op functions and save copies
    // of the original functions for our own use with modified names.
    this.editor.getSession().removeFoldReal = this.editor.getSession().removeFold;
    this.editor.getSession().addFoldReal = this.editor.getSession().addFold;
    this.editor.getSession().removeFold = function (_fold) {
      // disable normal fold removal logic when ace calls it
      // when we remove folds in our own code we use removeFoldReal()
    };
    this.editor.getSession().expandFold = function (_fold) {
      // disable normal fold expansion logic when ace calls it
    };
    this.editor.getSession().addFold = function (_fold) {
      // disable normal fold addition logic when ace calls it
    };

    // We monkey patch ace's rendering of folds so that we can render a pil with the property name
    // and icon instead. For "blank" properties and the last hovered property (the ones the user can
    // click and interact with) we attach a special class (CLASS_PROPERTY_DROPDOWN). There is logic
    // in `onAfterRender` to overlay `mp-query-block`s on these folds.
    const realRenderToken = this.editor.renderer.$textLayer.$renderToken.bind(this.editor.renderer.$textLayer);
    this.editor.renderer.$textLayer.$renderToken = function (this: TODO, stringBuilder, screenColumn, token, value) {
      if (token.type === `fold`) {
        let hovered = false;
        let key = token.value.trim();
        if (key[0] === `*`) {
          hovered = true;
          key = key.substring(1);
        }
        const property = this.composedProperties[key];
        const width = `${token.value.length * this.editor.renderer.$textLayer.config.characterWidth}px`;
        if (!hovered && property.label) {
          const icon = this.getPropertyButtonIcon(property);
          stringBuilder.push(
            `<span class="ace_fold" style="width:`,
            width,
            `;">  <svg-icon icon="`,
            icon,
            `"></svg-icon><span class="ace_fold_text">`,
            escapeHtml(computePropertyName(property)),
            `</span></span>`,
          );
        } else {
          stringBuilder.push(`<span class="ace_fold `, CLASS_PROPERTY_DROPDOWN, `" style="width:`, width, `;"></span>`);
        }
        return screenColumn + value.length;
      }
      return realRenderToken(stringBuilder, screenColumn, token, value);
    }.bind(this);
  }

  private updateEmptyMessage() {
    const value = this.editor.getValue();
    if (value === `` && !this.emptyMessageElem) {
      const emptyMessageElem = document.createElement(`div`);
      emptyMessageElem.className = `ace_empty_message`;
      emptyMessageElem.textContent = `Transform properties by typing a formula here or select an example above`;
      this.emptyMessageElem = emptyMessageElem;
      this.editor.renderer.content.appendChild(this.emptyMessageElem);
    } else if (value !== `` && this.emptyMessageElem) {
      this.emptyMessageElem.remove();
      this.emptyMessageElem = null;
    }
  }

  focus() {
    this.editor.focus();
  }

  disconnectedCallback() {
    StackableComponent.deleteCurrentStackingContextTarget(this.toString());
  }

  // TODO opslead
  // eslint-disable-next-line sonarjs/cognitive-complexity
  connectedCallback() {
    const divElem = document.createElement(`div`);
    const textElem = document.createTextNode(``);
    divElem.appendChild(textElem);
    this.editor = ace.edit(divElem);
    this.editor.setTheme(`ace/theme/mixpanel`);
    this.idleTimeout = null;

    this.hadInteraction = false;

    this.editor.$blockScrolling = Infinity;
    this.editor.setOptions({
      displayIndentGuides: false,
      enableBasicAutocompletion: true,
      enableLiveAutocompletion: true,
      highlightActiveLine: false,
      maxLines: `Infinity`,
      showFoldWidgets: false,
      showGutter: true,
      showPrintMargin: false,
      wrap: true,
    });
    this.editor.completers = [new CustomPropertyCompleter()];

    // syntax check worker must be disabled for our own use of setAnnotations to work
    this.editor.getSession().setUseWorker(false);
    this.editor.getSession().setUseWrapMode(true);
    this.editor.container.style.lineHeight = 1.91;
    this.editor.renderer.setPadding(10);
    this.editor.getSession().setMode(`ace/mode/arb-selector`);
    this.editor.getSession().setTabSize(2);
    this.editor.on(`blur`, () => {
      this.dispatchEvent(
        new CustomEvent(`blur`, {
          detail: {
            height: this.editor.getSession().getScreenLength() * (this.editor.renderer.lineHeight || 1.9),
          },
        }),
      );
    });
    this.editor.on(`change`, this.onChange.bind(this));
    this.editor.on(`focus`, () => {
      this.dispatchEvent(new CustomEvent(`focus`, {}));
    });
    this.editor.on(`blur`, () => {
      this.dispatchEvent(
        new CustomEvent(`autocompleteChange`, {
          detail: {
            menuTooltipData: null,
            cursorTooltipData: null,
          },
        }),
      );
    });
    this.editor.on(`mousemove`, (ev) => {
      // We replace the last hovered fold's normal simple div with an mp-query-block
      // so that it is editable, shows tooltips, etc. This callback is responsible for
      // editing the placeholder for the last hovered fold to begin with the `*`
      // character which is used as in indicator in the `renderToken` function that
      // this fold should render a query block instead of the normal div.
      const pos = ev.getDocumentPosition();
      const fold = this.editor.getSession().getFoldAt(pos.row, pos.column, 1);
      if (fold && this.lastHoveredFold !== fold) {
        if (this.lastHoveredFold) {
          const placeholder = `${this.lastHoveredFold.placeholder.substring(1)} `;
          const range = this.lastHoveredFold.range.clone();
          this.editor.getSession().removeFoldReal(this.lastHoveredFold);
          this.editor.getSession().addFoldReal(placeholder, range);
        }
        const placeholder = `*${fold.placeholder.trim()}`.padEnd(fold.placeholder.length, ` `);
        const range = fold.range.clone();
        this.editor.getSession().removeFoldReal(fold);
        this.lastHoveredFold = this.editor.getSession().addFoldReal(placeholder, range);
        this.editor.renderer.updateText();
      }
    });

    this.innerHTML = ``;
    this.appendChild(divElem);

    if (this.getAttribute(`display-formula`) !== ``) {
      const composedProperties = JSON.parse(this.getAttribute(`composed-properties`));
      this.populateDisplayFormula(this.getAttribute(`display-formula`), composedProperties);
      this.editor.completers[0].setComposedProperties(this.getAttribute(`project-id`), composedProperties);
    }
    this.editor.clearSelection();

    if (!autocompleteSingleton) {
      autocompleteSingleton = new Autocomplete();
    }
    this.editor.completer = autocompleteSingleton;
    this.editor.completer.editor = this.editor;

    const popup = this.editor.completer.getPopup();
    popup.renderer.on(`afterRender`, () => {
      // item being actively hovered with mouse takes priority,
      // otherwise use item currently selected via keyboard
      const row = popup.getHoveredRow() === -1 ? popup.getRow() : popup.getHoveredRow();
      const data = popup.getData(row);
      this.dispatchEvent(
        new CustomEvent(`autocompleteChange`, {
          detail: {
            autocompleteOpen: true,
            menuTooltipData: data && data.tooltip ? data.tooltip : null,
          },
        }),
      );
    });
    popup.on(`show`, () => {
      StackableComponent.setCurrentStackingContextTarget(this.toString());
    });
    popup.on(`hide`, () => {
      StackableComponent.deleteCurrentStackingContextTarget(this.toString());
      this.dispatchEvent(
        // clear the documentation tooltip if the autocomplete popup goes away
        new CustomEvent(`autocompleteChange`, {
          detail: {
            autocompleteOpen: false,
          },
        }),
      );
    });

    this.editor.getSession().selection.on(`changeCursor`, (_ev) => {
      const currFunc = this.getCurrentFunctionInfo(this.editor.getCursorPosition());
      let completions = null;
      if (currFunc) {
        completions = Object.keys(arbselectorDefs)
          .filter((key) => key.split(`(`)[0].toLowerCase() === currFunc.token.value.toLowerCase())
          .map((key) => ({
            header: key,
            subheader: arbselectorDefs[key][`syntax`],
            description: arbselectorDefs[key][`!doc`],
            details: [{icon: `info`, name: `Example`, value: arbselectorDefs[key][`examples`]}],
          }));
      }
      if (completions && completions.length > 0) {
        this.dispatchEvent(
          new CustomEvent(`autocompleteChange`, {
            detail: {
              currentAutocompleteLine: this.editor.container.getElementsByClassName(`ace_gutter-layer`)[0].children[
                currFunc.parenPos.row
              ],
              cursorTooltipData: completions[0],
            },
          }),
        );
      } else {
        this.dispatchEvent(
          new CustomEvent(`autocompleteChange`, {
            detail: {
              cursorTooltipData: null,
            },
          }),
        );
      }
    });

    this.monkeyPatchFoldLogic();

    this.editor.on(`change`, this.updateEmptyMessage.bind(this));
    this.updateEmptyMessage();

    this.editor.renderer.on(`afterRender`, this.onAfterRender.bind(this));
    this.editor.commands.addCommand({
      name: `addProperty`,
      bindKey: {win: `.`, mac: `.`},
      exec(editor) {
        const cursorPosition = editor.getCursorPosition();
        const token = editor.getSession().getTokenAt(cursorPosition.row, cursorPosition.column);
        if (
          !token ||
          (token.type !== `string.quoted` && token.type !== `comment` && token.type !== `constant.numeric`)
        ) {
          editor.session.insert(editor.getCursorPosition(), EMPTY_ENCODED_PROPERTY);
        } else {
          editor.session.insert(editor.getCursorPosition(), `.`);
        }
      },
    });

    this.dispatchEvent(
      new CustomEvent(`connect`, {
        detail: {
          autocompletePopup: popup.container,
          editor: this,
        },
      }),
    );

    this.ready = true;
    this.onChange();
  }

  private onChange() {
    // do not fire change events while component is still being setup
    if (!this.ready) {
      return;
    }

    this.scheduleSyntaxChecking();
    const data = this.extractDisplayFormula();
    if (data.persistableFormula !== ``) {
      this.hadInteraction = true;
    }
    this.editor.completers[0].setComposedProperties(this.getAttribute(`project-id`), data.properties);
    this.dispatchEvent(
      new CustomEvent(`change`, {
        detail: {
          displayFormula: data.persistableFormula,
          composedProperties: data.properties,
          extractionError: data.errors.length > 0 ? data.errors[0].message : null,
        },
      }),
    );
  }

  private createQueryBlock(encodedProperty) {
    const property = JSON.parse(encodedProperty);
    const queryBlock: QueryBlock = document.createElement(`mp-query-block`) as QueryBlock;
    const queryBlockAttrs: Partial<QueryBlockAttrs> = {
      'all-data-groups': false,
      'allow-typecast-pill-action': TypecastPillAction.Typecast,
      'event-names': JSON.stringify(this.attr(`event-names`)),
      'enable-workspace-filter-restrictions': this.attr(`enable-workspace-filter-restrictions`),
      'hide-edit-controls': true,
      'include-custom-properties': `persisted-only`,
      'input-only': !!property.label,
      label: computePropertyName(property) || property.label || ``,
      'prefix-label': ``,
      'project-has-cohorts': false,
      'hide-events': this.attr(`resource-type`) !== QUERY_BUILDER_RESOURCE_TYPE_MAPPING[ResourceType.Event],
      'project-has-people':
        // in workspace filters you cannot mix event and user properties
        !this.attr(`enable-workspace-filter-restrictions`) ||
        this.attr(`resource-type`) === QUERY_BUILDER_RESOURCE_TYPE_MAPPING[ResourceType.User],
      'project-id': this.attr(`project-id`),
      theme: QueryBlockThemeExtras.Builder,
      selected: encodedProperty,
      'selector-icon': this.getPropertyButtonIcon(JSON.parse(encodedProperty)),
      'show-all-tab': true,
      'show-special-date-property': true,
      'hide-special-date-property-unit': true,
      'track-name': this.attr(`track-name`),
      type: MenuType.Properties,
      'user-id': this.attr(`user-id`),
      'workspace-id': this.attr(`workspace-id`),
    };
    Object.entries(queryBlockAttrs).forEach((entry) => queryBlock.setAttribute(entry[0], entry[1]));
    queryBlock.style.position = `absolute`;

    // @ts-ignore - TODO: don't arbitrarily add properties
    queryBlock.foldElement = null;
    queryBlock.addEventListener(`close`, (ev) => {
      // @ts-ignore
      const foldElement: HTMLElement = ev.target.foldElement;
      // if we are closing the editor and there is no selected property
      // remove the query block widget completely
      if (foldElement) {
        // `select` may have fired first, in which case the fold will
        // already have been replaced and will not be found
        const foldRef = this.foldFromElement(foldElement);
        if (foldRef) {
          const doc = this.editor.getSession().getDocument();
          if (doc.getTextRange(foldRef.range) === EMPTY_ENCODED_PROPERTY) {
            doc.remove(foldRef.range);
          }
        }
      }
      // return focus to the editor after the drop-down closes
      this.editor.focus();
    });
    queryBlock.addEventListener(`select`, (ev: CustomEvent) => {
      // @ts-ignore
      const foldElement: HTMLElement = ev.target.foldElement;
      if (foldElement) {
        const foldRef = this.foldFromElement(foldElement);
        this.replaceRange(foldRef.range, `@"""${stringify(ev.detail.item)}"""@`);
      }
    });
    return queryBlock;
  }

  // TODO opslead
  // eslint-disable-next-line sonarjs/cognitive-complexity
  private onAfterRender(_ev, renderer) {
    const cont = this.editor.container.parentElement;

    let buttonDiv = cont.querySelector(`.mp-query-block-container`);
    if (!buttonDiv) {
      buttonDiv = document.createElement(`div`);
      buttonDiv.classList.add(`mp-query-block-container`);
      cont.appendChild(buttonDiv);
    }

    const contRect = cont.getBoundingClientRect();
    const folds = renderer.$textLayer.element.querySelectorAll(`.${CLASS_PROPERTY_DROPDOWN}`);
    folds.forEach((fold, idx) => {
      const foldRef = this.foldFromElement(fold);
      const foldText = foldRef
        ? this.editor.getSession().getDocument().getTextRange(foldRef.range)
        : `@"""{"err":"err"}"""@`;
      const encodedProperty = foldText.substring(4, foldText.length - 4);

      let oldQueryBlock = null;
      let newQueryBlock = null;
      if (idx >= buttonDiv.childNodes.length) {
        newQueryBlock = this.createQueryBlock(encodedProperty);
      } else if (foldRef === this.lastHoveredFold) {
        oldQueryBlock = buttonDiv.childNodes[idx];
        newQueryBlock = this.createQueryBlock(encodedProperty);
      } else {
        oldQueryBlock = newQueryBlock = buttonDiv.childNodes[idx];
        newQueryBlock.setAttribute(`selected`, encodedProperty);
        newQueryBlock.setAttribute(`selector-icon`, this.getPropertyButtonIcon(JSON.parse(encodedProperty)));
        newQueryBlock.removeAttribute(`input-only`);
        newQueryBlock.removeAttribute(`label`);
      }
      newQueryBlock.foldElement = fold;

      if (encodedProperty === `{}`) {
        newQueryBlock.setAttribute(`open`, true);
      }

      const foldRect = fold.getBoundingClientRect();
      newQueryBlock.style.top = `${foldRect.top - contRect.top + 7}px`;
      newQueryBlock.style.left = `${foldRect.left - contRect.left + 10}px`;
      if (oldQueryBlock !== newQueryBlock) {
        if (oldQueryBlock === null) {
          buttonDiv.appendChild(newQueryBlock);
        } else {
          buttonDiv.replaceChild(newQueryBlock, oldQueryBlock);
        }
      }
    });
    while (folds.length < buttonDiv.childNodes.length) {
      buttonDiv.removeChild(buttonDiv.lastChild);
    }
  }

  private foldFromElement(elem: HTMLElement) {
    const renderer = this.editor.renderer;
    const foldRect = elem.getBoundingClientRect();
    const foldPos = this.editor.renderer.screenToTextCoordinates(
      foldRect.left + renderer.characterWidth / 2,
      foldRect.top + renderer.lineHeight / 2,
    );
    return this.editor.getSession().getFoldAt(foldPos.row, foldPos.column, 1);
  }

  getCurrentFunctionInfo(startingPos) {
    if (startingPos.column === 0 && this.editor.session.getLine(startingPos.row).length === 0) {
      startingPos = this.calculatePreviousPosition(startingPos);
    }
    const prevPos = this.calculatePreviousPosition(startingPos);
    const prevChar = this.editor.session.getLine(prevPos.row).charAt(prevPos.column);
    let parenPos = null;
    if (prevChar === `(`) {
      parenPos = startingPos;
    } else {
      parenPos = this.editor.session.$findOpeningBracket(`)`, startingPos, /text/);
      if (parenPos && prevChar === `)`) {
        parenPos = this.editor.session.$findOpeningBracket(`)`, parenPos, /text/);
      }
    }
    if (parenPos) {
      let ch = ` `;
      while (ch === ` `) {
        parenPos = this.calculatePreviousPosition(parenPos);
        ch = this.editor.session.getLine(parenPos.row).charAt(parenPos.column);
      }
      // Handle cases where parens aren't part of a function call, e.g.
      // STRING(A * (B + C))
      // STRING((A))
      const token = this.editor.session.getTokenAt(parenPos.row, parenPos.column);
      if (token?.type === `entity.name.function`) {
        return {token, parenPos};
      } else if (parenPos.row !== 0 && parenPos.column !== 0) {
        return this.getCurrentFunctionInfo(parenPos);
      }
    }
    return null;
  }

  private calculatePreviousPosition(pos) {
    if (pos.column !== 0) {
      return {
        row: pos.row,
        column: pos.column - 1,
      };
    } else if (pos.row === 0) {
      return {
        row: 0,
        column: 0,
      };
    } else {
      return {
        row: pos.row - 1,
        column: this.editor.session.getLine(pos.row - 1).length,
      };
    }
  }

  private mergeComposedProperty(composedProperties, composedPropertiesReverseLookup, newProperty): string {
    const stableStringifiedProperty = stringify(newProperty);
    if (stableStringifiedProperty in composedPropertiesReverseLookup) {
      return composedPropertiesReverseLookup[stableStringifiedProperty];
    }

    const alphaIndex = composedPropertiesReverseLookup[`highest_alpha_index`] + 1 || 0;
    composedPropertiesReverseLookup[`highest_alpha_index`] = alphaIndex;
    const alphaLabel = `_` + getAlphaLabelFromIndex(alphaIndex);
    composedProperties[alphaLabel] = newProperty;
    composedPropertiesReverseLookup[stableStringifiedProperty] = alphaLabel;
    return alphaLabel;
  }

  private recomputeFolds(expectedFolds) {
    const foldsInDocument = this.editor.getSession().getAllFolds();
    for (const foldInDocument of foldsInDocument) {
      let expected = false;
      for (const expectedFold of expectedFolds) {
        if (
          foldInDocument.placeholder === expectedFold.placeholder &&
          foldInDocument.range.isEqual(expectedFold.range)
        ) {
          expected = true;
          expectedFold.found = true;
          break;
        }
      }
      if (!expected) {
        this.editor.getSession().removeFoldReal(foldInDocument);
      }
    }

    for (const expectedFold of expectedFolds) {
      if (!expectedFold.found) {
        this.editor.getSession().addFoldReal(expectedFold.placeholder, expectedFold.range);
      }
    }
    this.lastHoveredFold = null;
  }

  private extractDisplayFormula() {
    const session = this.editor.getSession();
    const tokenIter = new TokenIterator(session, -1, 0);
    const persistableFormula = [];
    const checkableFormula = [];
    const errors = [];
    const properties = {};
    const propertiesReverseLookup = {};
    const expectedFolds = [];
    let startPos = {
      row: 0,
      column: 0,
    };
    for (let token = tokenIter.stepForward(); token !== null; token = tokenIter.stepForward()) {
      if (isPropertyToken(token.type)) {
        const tokenRange = tokenIter.getCurrentTokenRange();
        const textRange = new range.Range(startPos.row, startPos.column, tokenRange.start.row, tokenRange.start.column);
        persistableFormula.push(session.getDocument().getTextRange(textRange));
        checkableFormula.push(session.getDocument().getTextRange(textRange));
        startPos = tokenRange.end;

        const tokenText = session.getDocument().getTextRange(tokenRange);
        const encodedProperty = tokenText.substring(4, tokenText.length - 4);
        const property = JSON.parse(encodedProperty);

        // The sizing here is based on how big the text that represents the property will be. The fold must
        // be at least large enough to hold the property name in the correct font plus some buffer for the
        // property icon. This then needs to be rounded up to the nearest multiple of the editor's monospace
        // font width (not rounding to font width would mis-align neighboring text in the editor).
        const label = computePropertyName(property) || `Select a property`;
        const textWidth = MeasureUtil.measureTextWidth(label, `600 14px ${mpFonts.fontFamilyPrimary}`);
        const characterWidth = this.editor.renderer.characterWidth || DEFAULT_CHARACTER_WIDTH;
        const placeholderChars = 5 + Math.ceil(textWidth / characterWidth);
        const alphaLabel = this.mergeComposedProperty(properties, propertiesReverseLookup, property);
        expectedFolds.push({
          placeholder: `${alphaLabel}`.padEnd(placeholderChars, ` `),
          range: tokenRange,
        });

        switch (token.type) {
          case `encoded_property`: {
            if (Object.entries(property).length === 0 && tokenText === PERSISTENT_EMPTY_ENCODED_PROPERTY) {
              errors.push({
                first_line: tokenRange.start.row,
                first_column: tokenRange.start.column,
                last_line: tokenRange.end.row,
                last_column: tokenRange.end.column,
                message: `Select a value for this property.`,
              });
            }
            persistableFormula.push(` ${alphaLabel} `);
            checkableFormula.push(` ${alphaLabel}${` `.repeat(tokenText.length - alphaLabel.length - 2)} `);
            break;
          }

          case `comment.encoded_property`:
            errors.push({
              first_line: tokenRange.start.row,
              first_column: tokenRange.start.column,
              last_line: tokenRange.end.row,
              last_column: tokenRange.end.column,
              message: `Properties cannot be referenced inside of comments.`,
            });
            break;

          case `string.encoded_property`:
            errors.push({
              first_line: tokenRange.start.row,
              first_column: tokenRange.start.column,
              last_line: tokenRange.end.row,
              last_column: tokenRange.end.column,
              message: `Properties cannot be referenced inside of quotes.`,
            });
            break;
        }
      }
    }
    this.recomputeFolds(expectedFolds);
    this.composedProperties = properties;

    const lastRow = session.getDocument().getLength();
    const textRange = new range.Range(
      startPos.row,
      startPos.column,
      lastRow,
      session.getDocument().getLine(lastRow).length,
    );
    persistableFormula.push(session.getDocument().getTextRange(textRange));
    checkableFormula.push(session.getDocument().getTextRange(textRange));
    return {
      persistableFormula: persistableFormula.join(``),
      checkableFormula: checkableFormula.join(``),
      properties,
      errors,
    };
  }

  private populateDisplayFormula(displayFormula, composedProperties) {
    this.editor.setValue(displayFormula, -1);

    // replacements must be done from the end of the document to the start or they will overwrite eachother
    // we gather them in reverse order in 'replacements'
    const replacements = [];
    const tokenIter = new TokenIterator(this.editor.getSession(), -1, 0);
    for (let token = tokenIter.stepForward(); token !== null; token = tokenIter.stepForward()) {
      if (token.type === `variable.other` && token.value.toUpperCase() in composedProperties) {
        const tokenRange = tokenIter.getCurrentTokenRange().clone();
        const line = this.editor.getSession().getDocument().getLine(tokenRange.start.row);
        if (tokenRange.start.column > 0 && line[tokenRange.start.column - 1] === ` `) {
          tokenRange.start.column--;
        }
        if (tokenRange.end.column < line.length && line[tokenRange.end.column] === ` `) {
          tokenRange.end.column++;
        }
        replacements.unshift([tokenRange, `@"""${stringify(composedProperties[token.value.toUpperCase()])}"""@`]);
      }
    }
    for (const replacement of replacements) {
      this.replaceRange(replacement[0], replacement[1]);
    }
  }

  private replaceRange(range, text: string) {
    const startPos = {
      row: range.start.row,
      column: range.start.column,
    };
    this.editor.getSession().getDocument().remove(range);
    this.editor.getSession().getDocument().insert(startPos, text);
  }

  private getPropertyButtonIcon(property) {
    if (property && property.resourceType === ResourceType.Event) {
      return `event-attribute`;
    } else {
      return `profile-attribute`;
    }
  }

  private async getSyntaxErrors(projectId, formula, variableSubstitutions, extractionErrors) {
    // If we could not properly extract the display formula there is no point in
    // syntax checking the result.
    if (extractionErrors.length > 0) {
      return extractionErrors;
    }

    const resp = await customPropertiesValidateClient.query({
      projectId,
      selector: formula,
      variableSubstitutions,
    });
    return resp.syntax_errors ? resp.syntax_errors : [];
  }

  // TODO opslead
  // eslint-disable-next-line sonarjs/cognitive-complexity
  private scheduleSyntaxChecking() {
    // clear any previous marked errors, since contents have changed they may no longer be valid
    this.editor.getSession().setAnnotations([]);
    for (const entry of Object.entries(this.editor.getSession().getMarkers(true))) {
      const markerId = entry[0];
      const marker: any = entry[1];
      if (marker.clazz === CLASS_SYNTAX_ERROR) {
        this.editor.getSession().removeMarker(markerId);
      }
    }

    // clear previously scheduled syntax checking (putting in some delay stops us from
    // checking after every keystroke)
    clearTimeout(this.idleTimeout);
    this.idleTimeout = null;

    const rawFormula = this.editor.getValue();
    if (rawFormula !== ``) {
      const data = this.extractDisplayFormula();
      const projectId = this.getAttribute(`project-id`);
      const variableSubstitutions = Object.entries(data.properties).reduce(
        (result, arr) => Object.assign(result, {[arr[0]]: {substitution: `unused`}}),
        {},
      );
      this.idleTimeout = setTimeout(() => {
        (async () => {
          const syntaxErrors = await this.getSyntaxErrors(
            projectId,
            data.checkableFormula,
            variableSubstitutions,
            data.errors,
          );
          // if the formula has changed since we issued our validation query then our
          // results are no longer useful, another query will already have been issued
          const currData = this.extractDisplayFormula();
          const currProps = Object.entries(currData.properties).reduce(
            (result, arr) => Object.assign(result, {[arr[0]]: `unused`}),
            {},
          );
          if (
            syntaxErrors.length > 0 &&
            data.checkableFormula === currData.checkableFormula &&
            stringify(Object.keys(variableSubstitutions)) === stringify(Object.keys(currProps))
          ) {
            const annotations = [];
            for (const err of syntaxErrors) {
              annotations.push({
                row: err[`first_line`],
                column: err[`first_column`],
                type: `error`,
                text: `\u2022 ` + err[`message`],
              });
              let errRange = new range.Range(
                err[`first_line`],
                err[`first_column`],
                err[`last_line`],
                err[`last_column`] + 1,
              );
              const fold = this.editor.getSession().getFoldAt(errRange.start.row, errRange.start.column, 1);
              if (fold) {
                errRange = fold.range.clone();
              }
              this.editor.getSession().addMarker(errRange, CLASS_SYNTAX_ERROR, `text`, true);
            }
            this.editor.getSession().setAnnotations(annotations);
          }
        })();
      }, 250);
    }
  }
}

export const EditorEl = defineAndMakeJsxFactory(`custom-property-ace-editor`, Editor);
